import { File, Swap, X } from "@phosphor-icons/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { PixelRect, Size } from "../../lib/selection-geometry";

const MENU_WIDTH = 200;
const GAP = 12;
const MENU_ITEM_CLASS =
  "flex w-full items-center gap-3 px-4 py-3 text-left text-body-small font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--accent-secondary-hover)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

/**
 * Place the menu beside the selection, preferring its left as in the design and
 * flipping to the right when there is no room. Coordinates are in frame space;
 * the menu is rendered in the stage's unclipped wrapper so it is never cut off.
 */
function menuPosition(anchor: PixelRect, frame: Size, menuHeight: number) {
  const toLeft = anchor.x - MENU_WIDTH - GAP;
  const left = toLeft >= 0 ? toLeft : anchor.x + anchor.width + GAP;
  const maxTop = Math.max(0, frame.height - menuHeight);
  return {
    left: Math.min(left, frame.width - MENU_WIDTH),
    top: Math.min(Math.max(anchor.y, 0), maxTop),
  };
}

interface SelectionOptionsMenuProps {
  anchor: PixelRect;
  frame: Size;
  onCreateNewDesign: () => void;
  onReplaceWithAsset: () => void;
  onClose: () => void;
  /** Shown under the actions when the selection can't be used, e.g. too small. */
  validationMessage: string | null;
}

/** What you can do with a selection. */
export function SelectionOptionsMenu({
  anchor,
  frame,
  onCreateNewDesign,
  onReplaceWithAsset,
  onClose,
  validationMessage,
}: SelectionOptionsMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuHeight, setMenuHeight] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: validationMessage is the only content-driven height change.
  useLayoutEffect(() => {
    if (containerRef.current) setMenuHeight(containerRef.current.offsetHeight);
  }, [validationMessage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      role="menu"
      aria-label="Select options"
      className="absolute z-20 overflow-hidden rounded-lg border border-[var(--stroke-field)] bg-[var(--bg-modal)] shadow-lg"
      style={{ width: MENU_WIDTH, ...menuPosition(anchor, frame, menuHeight) }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-body-default font-medium text-[var(--text-primary)]">
          Select Options
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close select options"
          className="text-white/80 transition-colors hover:text-white"
        >
          <X size={16} />
        </button>
      </div>
      <button
        type="button"
        role="menuitem"
        disabled={Boolean(validationMessage)}
        title={validationMessage ?? undefined}
        onClick={onCreateNewDesign}
        className={MENU_ITEM_CLASS}
      >
        <File size={16} aria-hidden />
        Create New Design
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={Boolean(validationMessage)}
        title={validationMessage ?? undefined}
        onClick={onReplaceWithAsset}
        className={MENU_ITEM_CLASS}
      >
        <Swap size={16} aria-hidden />
        Replace with Asset
      </button>
      {validationMessage && (
        <p className="px-4 pb-3 text-body-small text-[var(--text-secondary)]">
          {validationMessage}
        </p>
      )}
    </div>
  );
}
